import { jsPDF } from 'jspdf';
import { addCanvasToSinglePagePdf, captureVoucherElement } from '../shared/voucherPdfUtils';

const BANK_LABELS = {
    Axis: 'Axis Bank',
    Bandhan: 'Bandhan Bank',
    BOI: 'Bank of India',
    HDFC: 'HDFC Bank',
    ICICI: 'ICICI Bank',
    IDFC: 'IDFC Bank',
    PNB: 'Punjab National Bank',
    SBI: 'State Bank of India',
    TDS: 'TDS',
};

export const formatIndianAmount = (amount) => {
    const value = Number(amount) || 0;
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const numberToWordsBelow1000 = (num) => {
    const ones = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
        'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num < 20) return ones[num];
    if (num < 100) {
        const remainder = num % 10;
        return `${tens[Math.floor(num / 10)]}${remainder ? ` ${ones[remainder]}` : ''}`.trim();
    }

    const remainder = num % 100;
    return `${ones[Math.floor(num / 100)]} Hundred${remainder ? ` ${numberToWordsBelow1000(remainder)}` : ''}`.trim();
};

export const numberToIndianWords = (amount) => {
    const value = Math.round(Number(amount) || 0);
    if (value === 0) return 'Zero Rupees Only';

    const parts = [];
    const crore = Math.floor(value / 10000000);
    const lakh = Math.floor((value % 10000000) / 100000);
    const thousand = Math.floor((value % 100000) / 1000);
    const remainder = value % 1000;

    if (crore) parts.push(`${numberToWordsBelow1000(crore)} Crore`);
    if (lakh) parts.push(`${numberToWordsBelow1000(lakh)} Lakh`);
    if (thousand) parts.push(`${numberToWordsBelow1000(thousand)} Thousand`);
    if (remainder) parts.push(numberToWordsBelow1000(remainder));

    const words = `${parts.join(' ')} Rupees Only`;
    return words.charAt(0).toLowerCase() + words.slice(1);
};

export const getBankLabel = (bankCode) => {
    if (!bankCode) return '';
    return BANK_LABELS[bankCode] || bankCode;
};

export const formatReceiptDate = (dateValue) => {
    if (!dateValue) return '';
    const raw = String(dateValue).trim();

    const dashMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) {
        return `${dashMatch[1]}-${dashMatch[2]}-${dashMatch[3]}`;
    }

    const dotMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dotMatch) {
        return `${dotMatch[1]}-${dotMatch[2]}-${dotMatch[3]}`;
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
};

const drawDescriptionRow = (doc, tableX, tableWidth, labelColWidth, y, label, value) => {
    const valueColWidth = tableWidth - labelColWidth;
    const valueLines = doc.splitTextToSize(String(value || ''), valueColWidth - 6);
    const rowHeight = Math.max(8, valueLines.length * 4 + 4);

    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.rect(tableX, y, tableWidth, rowHeight);
    doc.line(tableX + labelColWidth, y, tableX + labelColWidth, y + rowHeight);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(label, tableX + 3, y + 5.5);

    doc.setFont('helvetica', 'normal');
    doc.text(valueLines, tableX + labelColWidth + 3, y + 5.5);

    return y + rowHeight;
};

export const generateMoneyReceiptPdf = (data) => {
    if (!data) return;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let y = 18;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('MONEY RECEIPT', pageWidth / 2, y, { align: 'center' });
    y += 12;

    doc.setFontSize(11);
    doc.text('Explore TouristHub Holidays LLP', pageWidth / 2, y, { align: 'center' });
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Corp Office: 63 Ananda Palit Road, Kolkata-700150', pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('Registered Office: Asutosh Bhawan, M N Roy Road,', pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('Barendrapara, Kol- 700149', pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('GSTIN: 19AALFE8568J1ZM', pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('Ph: +91 98300 17303/+91 99037 38567', pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('E-Mail: touristhubinfo@gmail.com', pageWidth / 2, y, { align: 'center' });
    y += 4;
    doc.text('www.touristhubindia.com', pageWidth / 2, y, { align: 'center' });
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Description', margin, y);
    y += 6;

    const tableX = margin;
    const tableWidth = pageWidth - margin * 2;
    const labelColWidth = 52;

    const descriptionRows = [
        ['Confirmation ID', data.ConfirmationId || ''],
        ['Name', data.CustomerName || ''],
        ['Mobile', data.Mobile || ''],
        ['Date', formatReceiptDate(data.MRDate)],
        ['Destination', data.Destination || ''],
        ['Amount', formatIndianAmount(data.MRAmount)],
        ['In Words', numberToIndianWords(data.MRAmount)],
        ['Payment Method', data.ModeOfPayment || ''],
        ['Received bank', getBankLabel(data.ReceivedBank)],
    ];

    descriptionRows.forEach(([label, value]) => {
        y = drawDescriptionRow(doc, tableX, tableWidth, labelColWidth, y, label, value);
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(255, 75, 43);
    doc.text('Thank You!', pageWidth / 2, pageHeight - 12, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    doc.save(`Money_Receipt_${data.MRNo || data.ConfirmationId || 'receipt'}.pdf`);
};

export const exportMoneyReceiptPdfFromElement = async (element, data) => {
    if (!element || !data) return;

    const canvas = await captureVoucherElement(element);

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    addCanvasToSinglePagePdf(pdf, canvas, imgData);

    pdf.save(`Money_Receipt_${data.MRNo || data.ConfirmationId || 'receipt'}.pdf`);
};
